/** The film-negative strip at the top of the setup, editor and film
 * screens.
 *
 * A leading "Setup" cell, one snap-centered cell per frame, and a final
 * "Film" cell; the centered cell IS the current view. Each frame cell
 * shows the best available representation: clip (looping muted video) >
 * still > sketch > crossed placeholder. Swiping the strip changes the
 * frame: when a new cell settles in the middle, a bubbling
 * "frame-change" event fires with { position }. Tapping the end cells
 * fires "setup-open" / "film-open".
 *
 * Attribute: position — "setup", a frame number, or "film" (the
 * current view).
 */

import { assetKey } from "../data/client";
import { frameCount } from "../data/definitions";
import { t } from "../lib/i18n";
import { store } from "../store";

const SETTLE_MS = 120;

export class WorkshopFilmstrip extends HTMLElement {
  static observedAttributes = ["position"];

  #built = false;
  #track!: HTMLElement;
  #frameCells: HTMLElement[] = [];
  #cellByKey = new Map<string, HTMLElement>();
  #offStore: (() => void) | null = null;
  #scrollTimer: number | null = null;
  #programmaticUntil = 0;

  /** The current cell: "setup", "film", or a frame number. */
  get currentKey(): string {
    const raw = (this.getAttribute("position") ?? "1").trim();
    if (raw === "setup" || raw === "film") return raw;
    const parsed = parseInt(raw, 10) || 1;
    return String(
      Math.min(frameCount(store.definitions), Math.max(1, parsed)),
    );
  }

  connectedCallback(): void {
    if (!this.#built) this.#build();
    this.#offStore = store.onChange(() => {
      // Definitions may land after the first build; rebuild once the
      // real frame count is known (at most once per page load).
      if (
        store.definitions &&
        this.#frameCells.length !== frameCount(store.definitions)
      ) {
        this.#rebuild();
      }
      this.#syncCells();
    });
    this.#syncCells();
    // Center the current cell once layout has settled, and pin the
    // strip directly beneath the real header height.
    requestAnimationFrame(() => {
      const root = this.getRootNode() as ShadowRoot | Document;
      const header = root.querySelector?.("scapescene-header");
      if (header instanceof HTMLElement && header.offsetHeight > 0) {
        this.style.top = `${header.offsetHeight}px`;
      }
      this.#centerOn(this.currentKey, false);
    });
  }

  disconnectedCallback(): void {
    this.#offStore?.();
    this.#offStore = null;
    if (this.#scrollTimer !== null) {
      window.clearTimeout(this.#scrollTimer);
    }
  }

  attributeChangedCallback(name: string, old: string | null): void {
    if (!this.#built || name !== "position") return;
    if (old !== null) this.#centerOn(this.currentKey, true);
    this.#markCurrent();
  }

  #rebuild(): void {
    this.#built = false;
    this.#frameCells = [];
    this.#cellByKey.clear();
    this.#build();
    this.#markCurrent();
    this.#centerOn(this.currentKey, false);
  }

  #build(): void {
    this.#built = true;
    this.innerHTML = `
      <div class="filmstrip">
        <div class="filmstrip-holes" aria-hidden="true"></div>
        <div class="filmstrip-track" role="tablist"></div>
        <div class="filmstrip-holes" aria-hidden="true"></div>
      </div>
    `;
    this.#track = this.querySelector(".filmstrip-track") as HTMLElement;

    this.#addEndCell("setup", "⚙", "Group setup");

    const count = frameCount(store.definitions);
    for (let position = 1; position <= count; position += 1) {
      const cell = document.createElement("button");
      cell.type = "button";
      cell.className = "filmstrip-cell";
      cell.dataset.key = String(position);
      cell.dataset.position = String(position);
      cell.setAttribute("role", "tab");
      cell.setAttribute("aria-label", `${t("frameWord")} ${position}`);
      cell.innerHTML = `
        <span class="filmstrip-media"></span>
        <span class="filmstrip-number">${position}</span>
        <span class="status-dot"></span>
      `;
      cell.addEventListener("click", () =>
        this.#request(String(position)),
      );
      this.#track.appendChild(cell);
      this.#frameCells.push(cell);
      this.#cellByKey.set(String(position), cell);
    }

    this.#addEndCell("film", "▶", "The film");

    this.#track.addEventListener("scroll", () => this.#onScroll(), {
      passive: true,
    });
    this.#markCurrent();
  }

  /** The setup / film cells that bookend the frames. */
  #addEndCell(key: string, glyph: string, label: string): void {
    const cell = document.createElement("button");
    cell.type = "button";
    cell.className = `filmstrip-cell filmstrip-cell--${key}`;
    cell.dataset.key = key;
    cell.setAttribute("aria-label", label);
    cell.innerHTML = `
      <span class="filmstrip-media"><span class="filmstrip-glyph">${glyph}</span>
      </span>
      <span class="filmstrip-number">${key}</span>
    `;
    cell.addEventListener("click", () => this.#request(key));
    this.#track.appendChild(cell);
    this.#cellByKey.set(key, cell);
  }

  /** A swipe settled or a cell was tapped. */
  #request(key: string): void {
    if (key === this.currentKey) {
      this.#centerOn(key, true);
      return;
    }
    if (key === "setup" || key === "film") {
      this.dispatchEvent(
        new CustomEvent(`${key}-open`, { bubbles: true, composed: true }),
      );
      return;
    }
    this.dispatchEvent(
      new CustomEvent("frame-change", {
        detail: { position: parseInt(key, 10) },
        bubbles: true,
        composed: true,
      }),
    );
  }

  #onScroll(): void {
    if (performance.now() < this.#programmaticUntil) return;
    if (this.#scrollTimer !== null) window.clearTimeout(this.#scrollTimer);
    this.#scrollTimer = window.setTimeout(() => {
      this.#scrollTimer = null;
      const centered = this.#centeredCell();
      if (centered !== null && centered !== this.currentKey) {
        this.#request(centered);
      }
    }, SETTLE_MS);
  }

  /** Which frame cell currently sits under the strip's midpoint. The
   * end cells never trigger by swipe — only by tap. */
  #centeredCell(): string | null {
    const mid = this.#track.scrollLeft + this.#track.clientWidth / 2;
    for (const cell of this.#frameCells) {
      if (
        mid >= cell.offsetLeft &&
        mid < cell.offsetLeft + cell.offsetWidth
      ) {
        return cell.dataset.key ?? null;
      }
    }
    return null;
  }

  #centerOn(key: string, smooth: boolean): void {
    const cell = this.#cellByKey.get(key);
    if (!cell) return;
    const left =
      cell.offsetLeft + cell.offsetWidth / 2 - this.#track.clientWidth / 2;
    // Ignore the scroll events this programmatic move produces.
    this.#programmaticUntil = performance.now() + (smooth ? 600 : 200);
    this.#track.scrollTo({
      left,
      behavior: smooth ? "smooth" : "auto",
    });
  }

  #markCurrent(): void {
    const current = this.currentKey;
    for (const [key, cell] of this.#cellByKey) {
      cell.classList.toggle("is-current", key === current);
      if (cell.getAttribute("role") === "tab") {
        cell.setAttribute(
          "aria-selected",
          key === current ? "true" : "false",
        );
      }
    }
  }

  /** Update each frame cell's media to its best representation. */
  #syncCells(): void {
    for (const cell of this.#frameCells) {
      const position = parseInt(cell.dataset.position ?? "0", 10);
      const frame = store.frame(position);
      const media = cell.querySelector(
        ".filmstrip-media",
      ) as HTMLElement;
      const dot = cell.querySelector(".status-dot") as HTMLElement;
      // assetKey: presigned URLs differ on every poll for the same
      // object — comparing raw URLs would rebuild (and flicker) the
      // strip constantly.
      const signature = frame
        ? `${assetKey(frame.videoUrl)}|${assetKey(frame.stillUrl)}|${assetKey(frame.sketchUrl)}`
        : "";

      if (cell.dataset.signature !== signature) {
        cell.dataset.signature = signature;
        media.innerHTML = "";
        if (frame?.videoUrl) {
          const video = document.createElement("video");
          video.src = frame.videoUrl;
          video.muted = true;
          video.loop = true;
          video.autoplay = true;
          video.playsInline = true;
          video.setAttribute("playsinline", "");
          media.appendChild(video);
        } else if (frame?.stillUrl || frame?.sketchUrl) {
          const img = document.createElement("img");
          img.src = frame.stillUrl || frame.sketchUrl;
          img.alt = "";
          img.loading = "lazy";
          media.appendChild(img);
        } else {
          const empty = document.createElement("span");
          empty.className = "filmstrip-empty";
          media.appendChild(empty);
        }
      }

      const status = frame?.videoStatus ?? "idle";
      dot.classList.toggle(
        "is-busy",
        status === "making_still" || status === "making_clip",
      );
      dot.classList.toggle("is-ready", status === "clip_ready");
      dot.classList.toggle("is-error", status === "error");
      dot.style.visibility = status === "idle" ? "hidden" : "visible";
    }
  }
}

if (!customElements.get("scapescene-filmstrip")) {
  customElements.define("scapescene-filmstrip", WorkshopFilmstrip);
}
